/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Details/Components/ServiceEndpointsHub" />
import * as React from "react";

import * as Constants from "Constants";
import { ActionCreatorManager } from "DistributedTask/Flux/ActionCreatorManager";
import { StoreManager } from "DistributedTask/Flux/StoreManager";
import { SecurityNamespaceConstants } from "DistributedTaskUI/SecurityComponent/Constants";
import { ServiceEndpointActionsCreator } from "DistributedTaskUI/ServiceEndpoints/Details/Actions/ServiceEndpointActionsCreator";
import { DeleteServiceEndpointConfirmationDialog } from "DistributedTaskUI/ServiceEndpoints/Details/Components/DeleteServiceEndpointConfirmationDialog";
import { ToggleDisabledServiceEndpointConfirmationDialog } from "DistributedTaskUI/ServiceEndpoints/Details/Components/ToggleDisabledServiceEndpointConfirmationDialog";
import { EndpointChecksComponent } from "DistributedTaskUI/ServiceEndpoints/Details/Components/EndpointChecksComponent";
import { ServiceEndpointComponent } from "DistributedTaskUI/ServiceEndpoints/Details/Components/ServiceEndpointComponent";
import { ServiceEndpointRequestHistoryComponent } from "DistributedTaskUI/ServiceEndpoints/Details/Components/ServiceEndpointRequestHistoryComponent";
import { ServiceEndpointSecurityComponent } from "DistributedTaskUI/ServiceEndpoints/Details/Components/ServiceEndpointSecurityComponent";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Details/Resources";
import { IServiceEndpointComponentState, ServiceEndpointStore } from "DistributedTaskUI/ServiceEndpoints/Details/Stores/ServiceEndpointStore";
import { ServiceEndpointUtils } from "DistributedTaskUI/ServiceEndpoints/Details/Utils/ServiceEndpointUtils";
import * as ARMConstants from "DistributedTask/Common/AzureResourcesService/Constants";

import { Card } from "VSSUI/Card";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { MenuItemType } from "VSSUI/Menu";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { IVssContributedTab, Tab, TabBar, TabContent, TabSize } from "VSSUI/Tabs";
import { ZeroData } from "VSSUI/ZeroData";

import { ChecksIssuesMessageComponent } from "Checks/ChecksOverview/ChecksIssuesMessageComponent";
import { FeatureFlagConstants, ResourceTypeNames } from "Checks/ChecksOverview/Generated/Constants";
import { PerformanceConstants } from "DistributedTask/Common/SharedResources/Constants";
import { IReadonlyObservableArray, Observable, ObservableValue } from "VSS/Core/Observable";
import * as Utils_String from "VSS/Core/Util/String";
import { getTabs } from "VSS/Features/PlatformUI/ContributedTab";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Pill, PillSize } from "VSSUI/Pill";

export interface IServiceEndpointProps {
    id: string;
    name?: string;
    typeIconUrl?: string;
    onBackNavigation: () => void;
}

export class ServiceEndpointsHub extends VssComponent<IServiceEndpointProps, IServiceEndpointComponentState> {
    constructor(props: IServiceEndpointProps, context: IVssComponentContext) {
        super(props, context);
        this._historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        this._store = StoreManager.GetStore<ServiceEndpointStore>(ServiceEndpointStore, ServiceEndpointStore.getKey());
        this.state = this._store.getState();
        this.selectedTabId = new ObservableValue("overview");
        this._loggedPerfEvent = false;
        this._expandPanel = new ObservableValue(false);
        this._showDeleteEndpointDialog = new ObservableValue(false);
        this._showToggleDisabledEndpointDialog = new ObservableValue(false);
        this._showConvertToSecretEndpointDialog = new ObservableValue(false);
        this._serviceEndpointActionCreator = ActionCreatorManager.CreateActionCreator<ServiceEndpointActionsCreator, IVssPageContext>(
            ServiceEndpointActionsCreator,
            "service-endpoints-section-actionscreator",
            this.context.pageContext
        );
        this._tabProvider = getTabs(context.pageContext, "ms.vss-distributedtask-web.service-connections-tab-group");
        this._title = document.title;
        this._panelTitle = Resources.EditPanelTitle;
        this._showCheckIssueMessageCard = isFeatureFlagEnabled(
            context.pageContext,
            FeatureFlagConstants.EnableChecksScalabilityResourceIssues,
            false
        );
        this._pageEvents = new Observable<React.UIEvent<HTMLDivElement>>();
    }

    public componentDidMount(): void {
        this._store.addChangedListener(this._onStoreChanged);
        if (!this._loggedPerfEvent && this.state.dataLoaded) {
            const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            performanceService.endScenario(
                PerformanceConstants.ServiceEndpointOverviewFeature,
                PerformanceConstants.ServiceEndpointOverviewScenario,
                true
            );
            this._loggedPerfEvent = true;
        }
    }

    public componentDidUpdate(): void {
        if (!this._loggedPerfEvent && this.state.dataLoaded) {
            const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            performanceService.endScenario(
                PerformanceConstants.ServiceEndpointOverviewFeature,
                PerformanceConstants.ServiceEndpointOverviewScenario,
                true
            );
            this._loggedPerfEvent = true;
        }
    }

    public componentWillUnmount(): void {
        this._store.removeChangedListener(this._onStoreChanged);
        StoreManager.DeleteStore(ServiceEndpointStore, ServiceEndpointStore.getKey());
    }

    private _onStoreChanged = (): void => {
        const state = this._store.getState();
        this.setState(state);
    }

    public render(): React.ReactNode {
        if (!this.state.dataLoaded) {
            return (
                <Page className="flex-grow loading-page">
                    <Spinner className="loading-component" size={SpinnerSize.large} />
                </Page>
            );
        } else if (this.state.serviceEndpointNotFound) {
            const image = getAssetLocation(this.context.pageContext, "ms.vss-distributedtask-web/dt-resources-hub/Content/no-results.png");

            return (
                <Page className="flex-grow zero-data-page">
                    <Card className="no-resource-container-card bolt-card-no-vertical-padding" contentProps={{ contentPadding: false }}>
                        <ZeroData
                            className={"no-resources-container"}
                            imagePath={image}
                            imageAltText={Resources.NoResourceError}
                            primaryText={Resources.NoResourceError}
                        />
                    </Card>
                </Page>
            );
        } else {
            return (
                <Page
                    className="flex-grow service-connection-hub"
                    selectedTabId={this.selectedTabId}
                    tabProviders={this._tabProvider}
                    onScroll={this._onPageScroll}
                >
                    {this._getPageContent()}
                    {this._getEditPanel()}
                    {this._getDeleteEndpointConfirmationDialog()}
                    {this._getToggleDisabledEndpointConfirmationDialog()}
                    {this._getConvertToSecretEndpointConfirmationDialog()}
                </Page>
            );
        }
    }

    private _onPageScroll = (event: React.UIEvent<HTMLDivElement>): void => {
        if (
            isFeatureFlagEnabled(
                this.context.pageContext,
                Constants.ServiceEntpointFeatureFlags.EnableStreamlinedScrollEventsInServiceEndpointHistory
            )
        ) {
            this._pageEvents.notify(event, "scroll");
            return;
        }

        const target = event.currentTarget;
        if (!target) {
            return;
        }

        const scrollPositionFromBottom = target.clientHeight + target.scrollTop - target.scrollHeight;
        const scrollPositionToFetch = 20; // to load items just a few pixels before the scrolling reaches the end of the page

        if (scrollPositionFromBottom < -scrollPositionToFetch || scrollPositionToFetch < scrollPositionFromBottom) {
            return;
        }

        if (!this._requestHistoryRef.current) {
            return;
        }

        this._requestHistoryRef.current.fetchMoreItems();
    }

    private _getPageContent(): React.ReactNode {
        switch (this._getViewType()) {
            case Constants.ViewTypes.Security:
                return this._getSecurityComponent();

            default:
                return this._getTabbedContent();
        }
    }

    private _getTabbedContent(): React.ReactNode {
        return (
            <React.Fragment>
                <Header
                    title={this._getServiceEndpointTitle()}
                    titleSize={TitleSize.Large}
                    backButtonProps={{ onClick: this._handleBackClick }}
                    commandBarItems={this._getCommandBarItems()}
                    commandBarMoreButtonId="security-page-more-button-id"
                    ref={this._headerRef}
                />
                {this._getCheckIssueCardComponent()}
                {this.state.errorMessage && this._getErrorCardComponent()}
                <TabBar onSelectedTabChanged={this.onSelectedTabChanged} tabSize={TabSize.Tall}>
                    <Tab name={Resources.Overview} id="overview" />
                    <Tab name={Resources.UsageHistory} id="history" />
                    <Tab name={Resources.ApprovalsAndChecksMenu} id="checks" />
                </TabBar>
                <TabContent>
                    {this.selectedTabId.value === "overview" && this.getDetailsComponent()}
                    {this.selectedTabId.value === "history" && this.getRequestHistoryComponent()}
                    {this.selectedTabId.value === "checks" && this._getChecksComponent()}
                </TabContent>
            </React.Fragment>
        );
    }

    private _getCheckIssueCardComponent(): React.ReactNode {
        if (this._showCheckIssueMessageCard) {
            return <ChecksIssuesMessageComponent resourceTypeName={ResourceTypeNames.ServiceEndpoint} resourceIds={this.state.serviceEndpoint.id} />;
        }
    }

    private _getServiceEndpointTitle(): React.ReactNode {
        return (
            <div className="flex-row scroll-hidden">
                <span className="service-endpoint-detail-title">{this.state.serviceEndpoint && this.state.serviceEndpoint.name}</span>
                {this.state.serviceEndpoint.isShared && (
                    <Pill className="shared-button margin-left-8" excludeTabStop={true} size={PillSize.compact}>
                        {Resources.ServiceConnectionSharedTag}
                    </Pill>
                )}
                {this.state.serviceEndpoint.isOutdated && (
                    <Pill className="shared-button margin-left-8" excludeTabStop={true} size={PillSize.compact}>
                        {Resources.ServiceConnectionOutdatedTag}
                    </Pill>
                )}
            </div>
        );
    }

    private _getCommandBarItems(): IHeaderCommandBarItem[] {
        let commandBarItems: IHeaderCommandBarItem[] = [
            {
                id: "edit-service-connection",
                text: Resources.EditMenu,
                important: true,
                disabled: !this.state.editingEnabled,
                onActivate: () => {
                    this._panelTitle = Resources.EditPanelTitle;
                    this._expandPanel.value = true;
                },
                rank: 1
            }
        ];

        if (this.state.convertingToSecretEnabled) {
            commandBarItems.push({
                id: "service-connection-convert-to-secret",
                text: Resources.ConvertARMEndpointToUseSecretMessage,
                iconProps: {
                    iconName: "History"
                },
                important: false,
                isPrimary: false,
                onActivate: () => {
                    this._showConvertToSecretEndpointDialog.value = true;
                },
                rank: 4,
                tooltipProps: {
                    text: Resources.ConvertARMEndpointToUseSecretDetails
                }
            });
        }

        commandBarItems.push(
            {
                id: "service-connection-security",
                text: Resources.SecurityMenu,
                iconProps: {
                    iconName: "Lock"
                },
                important: false,
                isPrimary: false,
                onActivate: () => {
                    this._setViewType(Constants.ViewTypes.Security);
                },
                rank: 3
            },
            {
                id: "menu-divider-1",
                itemType: MenuItemType.Divider,
                important: false,
                rank: 6
            },
            {
                id: "delete-service-connection",
                text: Resources.DeleteMenu,
                iconProps: {
                    iconName: "Delete"
                },
                important: false,
                isPrimary: false,
                onActivate: () => {
                    this._showDeleteEndpointDialog.value = true;
                },
                rank: 7
            }
        );

        this._addServiceEndpointDisablingCommandBarItems(commandBarItems);

        return commandBarItems;
    }

    private _addServiceEndpointDisablingCommandBarItems(commandBarItems: IHeaderCommandBarItem[]): void {
        const isDisablingAllowed = isFeatureFlagEnabled(
            this.context.pageContext,
            Constants.ServiceEntpointFeatureFlags.AllowDisablingServiceEndpoints
        );
        const isArmEndpoint = this.state.serviceEndpoint.type === ARMConstants.Azurerm;
        if (!isDisablingAllowed || !isArmEndpoint) {
            return;
        }

        if (this.state.serviceEndpoint.isDisabled) {
            commandBarItems.push({
                id: "enable-service-connection",
                text: Resources.EnableMenu,
                iconProps: {
                    iconName: "Play"
                },
                itemType: MenuItemType.Normal,
                important: false,
                isPrimary: false,
                onActivate: () => {
                    this._showToggleDisabledEndpointDialog.value = true;
                },
                rank: 5
            });
        } else {
            commandBarItems.push({
                id: "disable-service-connection",
                text: Resources.DisableMenu,
                iconProps: {
                    iconName: "Pause"
                },
                itemType: MenuItemType.Normal,
                important: false,
                isPrimary: false,
                onActivate: () => {
                    this._showToggleDisabledEndpointDialog.value = true;
                },
                rank: 5
            });
        }
    }

    private _handleBackClick = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        let viewtype = this._getViewType();
        document.title = this._title;
        if (viewtype === Constants.ViewTypes.Security) {
            this._setViewType(undefined);
        } else {
            this.props.onBackNavigation();
        }
    }

    private getDetailsComponent(): JSX.Element {
        document.title = Utils_String.format(Resources.ServiceConnectionsPageTitle, this.state.serviceEndpoint.name, Resources.Overview);
        return (
            <ServiceEndpointComponent
                onFinalizeClick={() => {
                    this._panelTitle = Resources.FinalizePanelTitle;
                    this._expandPanel.value = true;
                }}
            />
        );
    }

    private _getSecurityComponent(): JSX.Element {
        document.title = Utils_String.format(Resources.ServiceConnectionsPageTitle, this.state.serviceEndpoint.name, Resources.SecurityMenu);
        return (
            <React.Fragment>
                <Header
                    title={Resources.SecurityMenu}
                    description={this.state.serviceEndpoint && this.state.serviceEndpoint.name}
                    titleSize={TitleSize.Large}
                    backButtonProps={{
                        onClick: this._handleBackClick,
                        id: "endpoint-level-security-page-id"
                    }}
                />
                {this.state.errorMessage && this._getErrorCardComponent()}
                <ServiceEndpointSecurityComponent />
            </React.Fragment>
        );
    }

    private getRequestHistoryComponent(): JSX.Element {
        document.title = Utils_String.format(Resources.ServiceConnectionsPageTitle, this.state.serviceEndpoint.name, Resources.UsageHistory);

        return (
            <ServiceEndpointRequestHistoryComponent
                ref={this._requestHistoryRef}
                endpointId={this.state.serviceEndpoint.id}
                parentEvents={this._pageEvents}
            />
        );
    }

    private _getChecksComponent(): JSX.Element {
        if (!this.state.serviceEndpoint) {
            return null;
        }
        let hasManagePermission = this._serviceEndpointActionCreator.checkManageEndpointPermission(
            this.state.serviceEndpoint.id,
            SecurityNamespaceConstants.AdminPermission
        );
        return (
            <React.Fragment>
                {this.state.errorMessage && this._getErrorCardComponent()}
                <EndpointChecksComponent
                    resourceId={this.state.serviceEndpoint.id.toString()}
                    resourceName={this.state.serviceEndpoint.name}
                    hasManagePermission={hasManagePermission}
                />
            </React.Fragment>
        );
    }

    private onSelectedTabChanged = (newTabId: string) => {
        this.selectedTabId.value = newTabId;
        this.setState(this._store.getState());
    }

    private _getEditPanel(): JSX.Element {
        return (
            <Observer isPanelOpen={this._expandPanel}>
                {(props: { isPanelOpen: boolean }) => {
                    if (props.isPanelOpen) {
                        return (
                            <WrappedComponent
                                dependencies={["ms.vss-distributedtask-web.dt-serviceendpoints-editor"]}
                                wrappedType={"endpoints-editor-panel"}
                                onPrimaryAction={() => {
                                    this._expandPanel.value = false;
                                    this._serviceEndpointActionCreator.getServiceEndpoint(this.state.serviceEndpoint.id);
                                }}
                                onDismiss={() => {
                                    this._expandPanel.value = false;
                                }}
                                panelTitle={this._panelTitle}
                                endpoint={this.state.serviceEndpoint}
                                disableAllPipelinesSetting={
                                    !this._serviceEndpointActionCreator.checkProjectLevelManagePermission(SecurityNamespaceConstants.AdminPermission)
                                }
                                panelDescription={ServiceEndpointUtils.getEndpointPanelDescription(this.state.serviceEndpoint)}
                            />
                        );
                    }

                    return null;
                }}
            </Observer>
        );
    }

    private _getErrorCardComponent(): React.ReactNode {
        return (
            <MessageCard
                className="service-endpoint-hub-error-card"
                onDismiss={() => {
                    this.setState({ errorMessage: "" });
                }}
                severity={MessageCardSeverity.Error}
            >
                {this.state.errorMessage}
            </MessageCard>
        );
    }

    private _getDeleteEndpointConfirmationDialog(): React.ReactNode {
        return (
            <Observer isDialogOpen={this._showDeleteEndpointDialog}>
                {(props: { isDialogOpen: boolean }) => {
                    return props.isDialogOpen ? (
                        <DeleteServiceEndpointConfirmationDialog
                            onDismiss={() => {
                                this._showDeleteEndpointDialog.value = false;
                                if (this._headerRef.current) {
                                    this._headerRef.current.focus({ commandBarItemId: "security-page-more-button-id" });
                                }
                            }}
                            primaryOnClick={this._deleteServiceConnection}
                            onBackNavigation={this._onBackNavigation}
                            serviceEndpoint={this.state.serviceEndpoint}
                        />
                    ) : null;
                }}
            </Observer>
        );
    }

    private _getToggleDisabledEndpointConfirmationDialog(): React.ReactNode {
        return (
            <Observer isDialogOpen={this._showToggleDisabledEndpointDialog}>
                {(props: { isDialogOpen: boolean }) => {
                    return props.isDialogOpen ? (
                        <ToggleDisabledServiceEndpointConfirmationDialog
                            onDismiss={() => {
                                this._showToggleDisabledEndpointDialog.value = false;
                                if (this._headerRef.current) {
                                    this._headerRef.current.focus({ commandBarItemId: "security-page-more-button-id" });
                                }
                            }}
                            primaryOnClick={this._toggleDisabledServiceConnection}
                            onBackNavigation={this._onBackNavigation}
                            serviceEndpoint={this.state.serviceEndpoint}
                        />
                    ) : null;
                }}
            </Observer>
        );
    }

    private _getConvertToSecretEndpointConfirmationDialog(): React.ReactNode {
        const dismiss = () => {
            this._showConvertToSecretEndpointDialog.value = false;
            if (this._headerRef.current) {
                this._headerRef.current.focus({ commandBarItemId: "security-page-more-button-id" });
            }
        };
        return (
            <Observer isDialogOpen={this._showConvertToSecretEndpointDialog}>
                {(props: { isDialogOpen: boolean }) => {
                    return props.isDialogOpen ? (
                        <React.Fragment>
                            <WrappedComponent
                                wrappedType={"service-endpoint-convert-to-use-secret"}
                                onDismiss={() => dismiss()}
                                primaryOnClick={() => {
                                    this._serviceEndpointActionCreator.getServiceEndpoint(this.state.serviceEndpoint.id);
                                    this._showConvertToSecretEndpointDialog.value = false;
                                    if (this._headerRef.current) {
                                        this._headerRef.current.focus({ commandBarItemId: "security-page-more-button-id" });
                                    }
                                }}
                                onBackNavigation={this._onBackNavigation}
                                serviceEndpoint={this.state.serviceEndpoint}
                            />
                        </React.Fragment>
                    ) : null;
                }}
            </Observer>
        );
    }

    private _deleteServiceConnection = (): void => {
        this._serviceEndpointActionCreator.deleteServiceEndpoint(this.state.serviceEndpoint.id).then(
            () => {
                this.props.onBackNavigation();
            },
            err => {
                this.setState({ errorMessage: err.message });
            }
        );
    }

    private _toggleDisabledServiceConnection = (): void => {
        const actionResultPromise = this.state.serviceEndpoint.isDisabled
            ? this._serviceEndpointActionCreator.enableServiceEndpoint(this.state.serviceEndpoint.id, this.state.serviceEndpoint)
            : this._serviceEndpointActionCreator.disableServiceEndpoint(this.state.serviceEndpoint.id, this.state.serviceEndpoint);
        actionResultPromise.then(
            () => {
                this.props.onBackNavigation();
            },
            err => {
                this.setState({ errorMessage: err.message });
            }
        );
    }

    private _onBackNavigation = (): void => {
        this.props.onBackNavigation();
    }

    private _getViewType(): string {
        let historyState = this._historyService.getState();
        return historyState.view;
    }

    private _setViewType(viewtype: string | undefined) {
        let historyState = this._historyService.getState();
        historyState.view = viewtype;
        this._historyService.pushState(historyState);
        this.setState({});
    }

    private _store: ServiceEndpointStore;
    private _serviceEndpointActionCreator: ServiceEndpointActionsCreator;
    private selectedTabId: ObservableValue<string>;
    private _loggedPerfEvent: boolean;
    private _expandPanel: ObservableValue<boolean>;
    private _panelTitle: string;
    private _showDeleteEndpointDialog: ObservableValue<boolean>;
    private _showToggleDisabledEndpointDialog: ObservableValue<boolean>;
    private _showConvertToSecretEndpointDialog: ObservableValue<boolean>;
    private _tabProvider: IReadonlyObservableArray<IVssContributedTab>;
    private _historyService: IVssHistoryService;
    private _title: string;
    private _headerRef = React.createRef<Header>();
    private _requestHistoryRef = React.createRef<ServiceEndpointRequestHistoryComponent>();
    private _showCheckIssueMessageCard: boolean;
    private _pageEvents: Observable<React.UIEvent<HTMLDivElement>>;
}
